import merge from 'lodash/merge';
import moment from 'moment';
import { addMany } from './helpers';

const state = () => ({
  allIds: [],
  byId: {}
});

const useExpirationSettings = op =>
  op.end_date
    ? moment().isAfter(op.end_date)
    : op.expire_date && moment().isAfter(op.expire_date);

const getTicketTimeout = op => {
  // Get possible timer override
  const defaultHideTicketSettings = {
    amount: 10,
    type: 'minutes'
  };

  const hideTicketSettings = useExpirationSettings(op)
    ? op?.product?.ticket_settings?.hide_ticket?.after_expiration
    : op?.product?.ticket_settings?.hide_ticket?.after_redemption;

  const { amount, type } = hideTicketSettings || defaultHideTicketSettings;

  let timeoutTime;
  if (!type) return undefined;

  switch (type) {
    case 'immediate':
      timeoutTime = 10 * 1000 * 60;
      break;
    case 'days':
      timeoutTime = Number(amount) * 1000 * 60 * 60 * 24;
      break;
    case 'hours':
      timeoutTime = Number(amount) * 1000 * 60 * 60;
      break;
    case 'minutes':
      timeoutTime = Number(amount) * 1000 * 60;
      break;
    default:
      timeoutTime = 10 * 1000 * 60;
      break;
  }

  return timeoutTime;
};

const getTicketTimer = op => {
  if (!op.redeemed || !op.ticket || !op.product?.ticket_settings)
    return undefined;

  // Check-ins set timer to 0
  if (op.redemptions === 'check-in') return 0;
  // Other redemptions set based on ticket settings

  const initialTime = !useExpirationSettings(op)
    ? moment.utc(op.redeemed).valueOf()
    : moment.utc(op.end_date || op.expire_date).valueOf();

  const nowTime = moment.utc().valueOf();
  const timeoutTime = getTicketTimeout(op);
  return timeoutTime - (nowTime - initialTime);
};

const getDuplicateDate = op => {
  if (!op?.product.repeat || !op.redeemed || op.duplicate_date)
    return op.duplicate_date;

  // Redeemed at this point is already in utc - don't use utc() here
  return moment(op.redeemed)
    .add(op.product.repeat_delay, op.product.repeat_delay_type)
    .format('YYYY-MM-DD HH:mm:ss');
};

const getters = {
  find: state => id => state.byId?.[id],
  byProductId: (state, getters) => (productId, dealId) =>
    getters.list
      .filter(
        op =>
          op.product_id === productId &&
          (dealId === undefined || op.deal_id === dealId)
      )
      .sort((a, b) => b.id - a.id), // Descending by id is expected by check-in-btn
  byDealId: (state, getters) => dealId =>
    getters.list.filter(op => op.deal_id === dealId),
  hasRedemptionsRemaining: (state, _, __, rootGetters) => orderProduct => {
    if (!orderProduct.order_deal_id) return true;

    // The purchased package's experience cap applies to the deal as a whole, so it holds even for
    // products that do not consume one of the deal's own redemptions - those skip the check below.
    if (
      rootGetters['orderDeals/isBlockedByPackageCap'](
        orderProduct.order_deal_id
      )
    )
      return false;

    return (
      orderProduct.redemptions_remaining_use === 0 ||
      rootGetters['orderDeals/hasRedemptionsRemaining'](
        orderProduct.order_deal_id
      )
    );
  },
  list: (state, getters) => state.allIds.map(id => getters.find(id)),
};

const actions = {
  addOrderProducts({ commit, dispatch, rootGetters }, orderProducts) {
    orderProducts.forEach(op => {
      op.product = rootGetters['products/find'](op.product_id);
      if (op.ticket) {
        op.ticket = {
          ...op.ticket,
          initialTimer: getTicketTimeout(op),
          timer: getTicketTimer(op),
          order_product_id: op.id
        };
      }
      op.duplicate_date = getDuplicateDate(op);
    });
    commit('addMany', orderProducts);
    dispatch('progressTracker/initializeProgressTracker', null, { root: true });
  },

  expireTicketTimer({ commit }, op) {
    commit('expireTicketTimer', op);
  },

  removeOrderProducts({ commit }, orderProductIds) {
    commit('removeAll', orderProductIds);
  },

  setRedeemed({ commit, state, dispatch }, orderProductId) {
    const orderProduct = state.byId[orderProductId];
    commit('setRedeemed', orderProduct);
    commit('setRepeat', orderProduct);
    commit('setDuplicateDate', orderProduct);
    commit('setTicketTimer', orderProduct);
    dispatch('progressTracker/recomputeProgress', null, { root: true });
  },

  update({ commit, state, dispatch }, orderProducts) {
    orderProducts.forEach(op => {
      commit('update', op);
      // Get updated op
      const orderProduct = state.byId[op.id];
      commit('setDuplicateDate', orderProduct);
      commit('setTicketTimer', orderProduct);
    });
    dispatch('progressTracker/recomputeProgress', null, { root: true });
  },

  updateTimers({ commit }, orderProducts) {
    orderProducts.forEach(op => commit('setTicketTimer', op));
  }
};

const mutations = {
  add(state, op) {
    if (!state.allIds.includes(op.id)) {
      state.byId[op.id] = op;
      state.allIds.push(op.id);
    } else {
      state.byId[op.id] = merge(state.byId[op.id], op);
    }
  },

  addMany(state, items) {
    addMany(state, items);
  },

  expireTicketTimer(state, op) {
    if (state.byId[op.id]?.ticket) {
      state.byId[op.id].ticket.timer = -1;
    }
  },

  removeAll(state, orderProductIds) {
    orderProductIds.forEach(id => {
      delete state.byId[id];
    });

    state.allIds = state.allIds.filter(
      id => !orderProductIds.includes(id)
    );
  },

  setDuplicateDate(state, op) {
    state.byId[op.id].duplicate_date = getDuplicateDate(op);
  },

  setRedeemed(state, op) {
    // Set it just like it would be coming from the server in utc time
    state.byId[op.id].redeemed = moment
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');
  },

  setRepeat(state, op) {
    state.byId[op.id].repeatDate = op?.duplicate_date
      ? moment.utc(op.duplicate_date).format('YYYY-MM-DD HH:mm:ss')
      : null;
  },

  setTicketTimer(state, op) {
    if (!state.byId[op.id]?.ticket) return;
    state.byId[op.id].ticket.timer = getTicketTimer(op);
  },

  // To access root state - this.state
  update(state, op) {
    state.byId[op.id] = merge(state.byId[op.id], op);

    // Get offer - we need to know if this is a preview
    // TODO: There must be a way to make this easier, probably isPreview should be
    // placed directly on rootState
    // const offer = rootState.offers.byId[rootState.offers.allIds[0]];

    if (this.state.offer.isPreview) {
      const product = state.byId[op.id].product;

      product.ticket_settings ??= {};
      product.ticket_settings.hide_ticket ??= {};

      product.ticket_settings.hide_ticket.after_redemption = {
        ...(product.ticket_settings.hide_ticket.after_redemption || {}),
        amount: 0.25,
        type: 'minutes'
      };
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
